import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  chakra,
} from "@chakra-ui/react";
import React, { ChangeEvent, useCallback } from "react";

export interface TableSearchBoxProps<T = string, B = string> extends Omit<BoxProps, "onChange"> {
  value: T;
  onChange: (newValue: T) => void;
  searchEnum?: [T, string][]; // [value, label][]
  searchField?: B;
  searchFields?: [B, string][]; // [value, label][]
  setSearchField?: (field: B) => void;
  helpText?: string;
  placeholder?: string;
  descriptionLabel?: string;
}

export const TableSearchBox = <T extends string = string, B extends string = string>({
  value,
  onChange,
  searchEnum,
  searchFields,
  searchField,
  setSearchField,
  helpText,
  minWidth = "30rem",
  placeholder,
  descriptionLabel,
  ...props
}: TableSearchBoxProps<T, B>) => {
  const onChangeEvent = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value as T),
    [onChange]
  );

  const onClear = useCallback(() => onChange("" as T), [onChange]);

  return (
    <Box minWidth={minWidth} {...props}>
      <Flex>
        {searchEnum ? (
          <Select
            isRequired={false}
            value={value}
            onChange={(event) => onChange && onChange(event.target.value as T)}
            minWidth="20rem"
          >
            {[["", "---"], ...searchEnum].map(([value, description], idx) => (
              <option key={`${value}-${idx}`} value={value}>
                {description}
              </option>
            ))}
          </Select>
        ) : (
          <InputGroup>
            <InputLeftElement color="gray.300" children={<SearchIcon />} />

            <Input
              value={value}
              onChange={onChangeEvent}
              placeholder={placeholder}
              minWidth="20rem"
              isRequired={false}
            />
            {value && (
              <InputRightElement cursor="pointer" color="gray.300">
                <CloseIcon onClick={onClear} />
              </InputRightElement>
            )}
          </InputGroup>
        )}
        {searchFields && searchFields.length > 0 ? (
          <Select
            isRequired={false}
            marginLeft="0.5rem"
            value={searchField || searchFields[0][0]}
            onChange={(event) => setSearchField && setSearchField(event.target.value as B)}
            maxWidth="15rem"
            borderRadius="3px"
          >
            {searchFields.map(([value, description], idx) => (
              <option key={`${value}-${idx}`} value={value}>
                {descriptionLabel || "Search"}: {description}
              </option>
            ))}
          </Select>
        ) : null}
      </Flex>
      {helpText ? <chakra.span fontStyle="italic">{helpText}</chakra.span> : null}
    </Box>
  );
};
